import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, IconButton, Tooltip } from "@mui/material";
import { usePermissions } from "@/hooks/usePermissions";
import { useResendInvitation } from "@/hooks/useUsers";
import type { ActionConfig, User } from "@/interfaces/user";

interface UserActionsProps {
  user: User;
  onImpersonate: (user: User) => void;
  onEditRole: (user: User) => void;
  onArchive: (user: User) => void;
}

export default function UserActions({
  user,
  onImpersonate,
  onEditRole,
  onArchive,
}: UserActionsProps) {
  const { can } = usePermissions();
  const canEdit = can("edit", "USER");
  const canArchive = can("delete", "USER");

  const { mutate: resendInvitation, isPending: isResending } = useResendInvitation();

  const actions: ActionConfig[] = [
    {
      key: "impersonate",
      title: "Impersonate user",
      ariaLabel: `Impersonate ${user.name}`,
      icon: <VisibilityIcon sx={{ fontSize: 16 }} />,
      visible: can("start", "IMPERSONATION") && !!user.clerkUserId,
      disabled: false,
      onClick: () => onImpersonate(user),
    },
    {
      key: "resend-invitation",
      title: "Resend invitation",
      ariaLabel: `Resend invitation to ${user.name}`,
      icon: <MailOutlineIcon sx={{ fontSize: 16 }} />,
      visible: user.verified === false && canEdit,
      disabled: isResending,
      onClick: () => resendInvitation(user.id),
    },
    {
      key: "edit-role",
      title: canEdit ? "Edit role" : "You don't have permission to edit roles",
      ariaLabel: `Edit role for ${user.name}`,
      icon: <EditOutlinedIcon sx={{ fontSize: 16 }} />,
      visible: true,
      disabled: !canEdit,
      onClick: () => onEditRole(user),
    },
    {
      key: "archive",
      title: canArchive ? "Archive user" : "You don't have permission to archive users",
      ariaLabel: `Archive user ${user.name}`,
      icon: <DeleteOutlineIcon sx={{ fontSize: 16 }} />,
      visible: true,
      disabled: !canArchive,
      onClick: () => onArchive(user),
      hoverBgColor: "var(--color-good-wine-bg)",
      hoverColor: "var(--color-good-wine)",
    },
  ];

  return (
    <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
      {actions
        .filter((action) => action.visible)
        .map((action) => (
          <Tooltip key={action.key} title={action.title}>
            <span>
              <IconButton
                onClick={action.onClick}
                disabled={action.disabled}
                size="small"
                aria-label={action.ariaLabel}
                sx={{
                  width: 36,
                  height: 36,
                  border: "1px solid var(--color-smoke)",
                  borderRadius: "var(--radius-btn)",
                  color: "var(--color-graphite)",
                  "&:hover": {
                    bgcolor: action.hoverBgColor ?? "var(--color-wax)",
                    color: action.hoverColor ?? "var(--color-rodeo-black)",
                  },
                  "&.Mui-disabled": {
                    color: "var(--color-wax)",
                    borderColor: "var(--color-wax)",
                  },
                }}
              >
                {action.icon}
              </IconButton>
            </span>
          </Tooltip>
        ))}
    </Box>
  );
}
